import * as React from 'react';
import classnames from 'classnames';

import ActionLink from 'components/buttons/action-link/ActionLink';
import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import InfluencerEntity from 'modules/influencers/components/influencer-entity/InfluencerEntity';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import CrossButton from 'components/buttons/cross-button/CrossButton';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { getEntityFromProfile } from 'utils/view';
import { __t, applyTranslations } from 'utils/language';

import type { Subcampaign } from 'types';
import { isDraft } from 'utils/subcampaign';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./subcampaigns-multi-select.i18n.json'));

type Props = {
  isLoading: boolean,
  subcampaigns: Array<Subcampaign>,
  selectedIdsMap?: {
    [k in number | string]: boolean;
  },
  disabledIdsMap?: {
    [k in number | string]: boolean;
  },
  titleAll: string,
  titleAllCollapsible?: boolean,
  titleAllWithCheckbox?: boolean,
  titleAllWithSelectAll?: boolean,
  titleSelected: string,
  messageAllEmpty: string,
  messageSelectedEmpty: string,
  children?: React.ReactNode,
  onToggle: (a: number) => void,
  onToggleAll?: (b: Array<number>, a: boolean) => void
};

type State = {
  isCollapsed: boolean
};

class SubcampaignsMultiSelect extends React.PureComponent<Props, State> {
  state = {
    isCollapsed: false
  };

  handleToggleCollapsingArrow = (): void => {
    this.setState(state => ({
      ...state,
      isCollapsed: !state.isCollapsed
    }));
  };

  handleToggle = (event: SyntheticEvent<HTMLElement>): void => {
    const { onToggle } = this.props;
    const subcampaignId = Number(event.currentTarget.dataset.id);

    onToggle(subcampaignId);
  };

  handleToggleAll = (isSelected: boolean): void => {
    const { subcampaigns, onToggleAll } = this.props;

    if (onToggleAll) {
      onToggleAll(subcampaigns.map(s => s.id), isSelected);
    }
  };

  handleSelectAll = (): void => {
    this.handleToggleAll(true);
  };

  handleDeselectAll = (): void => {
    this.handleToggleAll(false);
  };

  renderRow(subcampaign: Subcampaign, isSelected: boolean, isDisabled: boolean): React.ReactNode {
    const rowClasses = classnames({
      'subcampaigns-multi-select__row': true,
      'subcampaigns-multi-select__row--left': true,
      'subcampaigns-multi-select__row--disabled': isDisabled
    });

    const isNonDraftSubcampaign = !isDraft(subcampaign);

    return (
      <li
        key={`row-${subcampaign.id}`}
        className={rowClasses}
      >
        <InfluencerEntity
          influencerProfile={subcampaign.creatorProfile}
        />

        <div className="subcampaigns-multi-select__row-rest">
          {
            isNonDraftSubcampaign && subcampaign.campaign &&
              <ActionLink
                color="light"
                openInNewTab
                link={`/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}`}
              >
                {__t('subcampaigns_multi_select.button_view_offer')}
              </ActionLink>
          }
          <div className="subcampaigns-multi-select__row-checkbox">
            <InputCheckbox
              id={`row-checkbox-${subcampaign.id}-${Math.random()}`}
              data-id={subcampaign.id}
              checked={isSelected || false}
              onChange={this.handleToggle}
            />
          </div>
        </div>
      </li>
    );
  }

  renderSelectedRow(subcampaign: Subcampaign): React.ReactNode {
    const entity = getEntityFromProfile(subcampaign.creatorProfile);

    return (
      <li
        key={`row-selected-${subcampaign.id}`}
        className="subcampaigns-multi-select__row subcampaigns-multi-select__row--right"
      >
        <Entity>
          <EntityAvatar
            entity={entity}
            link={entity?.url}
            openInNewTab
            customSize={{
              width: 24,
              height: 24
            }}
          />
          <EntityName
            entity={entity}
            link={entity?.url}
            openInNewTab
            size="small"
            style={{
              maxWidth: 88
            }}
          />
        </Entity>

        <div className="subcampaigns-multi-select__cross">
          <CrossButton
            type="corner-small"
            data={{ id: subcampaign.id }}
            onClick={this.handleToggle}
          />
        </div>
      </li>
    );
  }

  renderLoadedRows(): React.ReactNode {
    const { subcampaigns, selectedIdsMap, disabledIdsMap, messageAllEmpty } = this.props;
    const { isCollapsed } = this.state;

    const rowClasses = classnames({
      'subcampaigns-multi-select__rows': true,
      'subcampaigns-multi-select__rows--collapsed': isCollapsed
    });

    return (
      subcampaigns.length > 0 ?
        <ul className={rowClasses}>
          {
            subcampaigns.map(subcampaign =>
              this.renderRow(
                subcampaign,
                selectedIdsMap && selectedIdsMap[subcampaign.id] || false,
                disabledIdsMap && disabledIdsMap[subcampaign.id] || false
              )
            )
          }
        </ul> :
        <div className="subcampaigns-multi-select__empty">
          {messageAllEmpty}
        </div>
    );
  }

  renderContent(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      isLoading ?
        <Spinner
          position="inline"
          size="small"
          color="light"
        /> :
        this.renderLoadedRows()
    );
  }

  renderTitleAll(areAllSelected: boolean): React.ReactNode | undefined | null {
    const { isLoading, titleAll, titleAllWithCheckbox, titleAllCollapsible, titleAllWithSelectAll, subcampaigns } = this.props;
    const { isCollapsed } = this.state;

    const hasData = subcampaigns.length > 0;

    const arrowClasses = classnames({
      'subcampaigns-multi-select__title-arrow': true,
      'subcampaigns-multi-select__title-arrow--collapsed': isCollapsed
    });

    if (isLoading) {
      return null;
    }

    return (
      <div className="subcampaigns-multi-select__title">
        {
          titleAllWithCheckbox && hasData &&
            <div className="subcampaigns-multi-select__title-checkbox">
              <InputCheckbox
                id={`bulk-multiselect-${Math.random()}`}
                checked={areAllSelected}
                onChange={areAllSelected ? this.handleDeselectAll : this.handleSelectAll}
              />
            </div>
        }
        <span className="subcampaigns-multi-select__title-label">
          {titleAll}
        </span>
        {
          titleAllCollapsible && hasData &&
            <div
              className={arrowClasses}
              onClick={this.handleToggleCollapsingArrow}
            >
              <SVGDynamicIcon
                name="chevron-down-custom-fg"
                width={13}
                height={6}
              />
            </div>
        }
        {
          titleAllWithSelectAll && hasData &&
            <div className="subcampaigns-multi-select__title-rest">
              {
                areAllSelected ?
                  <ActionLink
                    key="deselect-all"
                    color="light"
                    onClick={this.handleDeselectAll}
                  >
                    {__t('collabary_common.action_button.deselect_all')}
                  </ActionLink> :
                  <ActionLink
                    key="select-all"
                    color="light"
                    onClick={this.handleSelectAll}
                  >
                    {__t('collabary_common.action_button.select_all')}
                  </ActionLink>
              }
            </div>
        }
      </div>
    );
  }

  render() {
    const {
      subcampaigns,
      selectedIdsMap,
      disabledIdsMap,
      titleSelected,
      messageSelectedEmpty,
      children
    } = this.props;

    const selectedInfluencers = subcampaigns.filter(subcampaign => (selectedIdsMap ? selectedIdsMap[subcampaign.id] : true));
    const lockedInfluencers = subcampaigns.filter(subcampaign => (disabledIdsMap ? disabledIdsMap[subcampaign.id] : false));
    const areAllSelected = selectedInfluencers.length === subcampaigns.length - lockedInfluencers.length;

    return (
      <section className="subcampaigns-multi-select">
        <div className="subcampaigns-multi-select__column subcampaigns-multi-select__column--left">
          {
            children &&
              <div className="subcampaigns-multi-select__other">
                {children}
              </div>
          }
          {this.renderTitleAll(areAllSelected)}
          {this.renderContent()}
        </div>
        <div className="subcampaigns-multi-select__column subcampaigns-multi-select__column--right">
          <div className="subcampaigns-multi-select__title">
            {`${titleSelected} (${selectedInfluencers.length})`}
          </div>
          {
            selectedInfluencers.length > 0 ?
              <ul className="subcampaigns-multi-select__rows">
                {
                  selectedInfluencers
                    .map(subcampaign => this.renderSelectedRow(subcampaign))
                }
              </ul> :
              <div className="subcampaigns-multi-select__empty">
                {messageSelectedEmpty}
              </div>
          }
        </div>
      </section>
    );
  }
}

export default SubcampaignsMultiSelect;
